export interface IndexedPoint {
  x: number;
  y: number;
  index: number;
}

export interface SpatialBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

const NODE_CAPACITY = 16;
const MAX_DEPTH = 18;

function intersects(a: SpatialBounds, b: SpatialBounds): boolean {
  return a.x <= b.x + b.width && a.x + a.width >= b.x && a.y <= b.y + b.height && a.y + a.height >= b.y;
}

function contains(bounds: SpatialBounds, point: IndexedPoint): boolean {
  return point.x >= bounds.x && point.x <= bounds.x + bounds.width && point.y >= bounds.y && point.y <= bounds.y + bounds.height;
}

class QuadNode {
  readonly points: IndexedPoint[] = [];
  children: QuadNode[] | null = null;

  constructor(readonly bounds: SpatialBounds, readonly depth: number) {}

  insert(point: IndexedPoint): boolean {
    if (!contains(this.bounds, point)) return false;
    if (!this.children && (this.points.length < NODE_CAPACITY || this.depth >= MAX_DEPTH)) {
      this.points.push(point);
      return true;
    }
    if (!this.children) this.split();
    return this.children!.some((child) => child.insert(point));
  }

  query(range: SpatialBounds, result: IndexedPoint[]): void {
    if (!intersects(this.bounds, range)) return;
    for (const point of this.points) {
      if (contains(range, point)) result.push(point);
    }
    for (const child of this.children ?? []) child.query(range, result);
  }

  private split(): void {
    const halfWidth = this.bounds.width / 2;
    const halfHeight = this.bounds.height / 2;
    const { x, y } = this.bounds;
    this.children = [
      new QuadNode({ x, y, width: halfWidth, height: halfHeight }, this.depth + 1),
      new QuadNode({ x: x + halfWidth, y, width: halfWidth, height: halfHeight }, this.depth + 1),
      new QuadNode({ x, y: y + halfHeight, width: halfWidth, height: halfHeight }, this.depth + 1),
      new QuadNode({ x: x + halfWidth, y: y + halfHeight, width: halfWidth, height: halfHeight }, this.depth + 1),
    ];
    const existing = this.points.splice(0);
    for (const point of existing) this.children.some((child) => child.insert(point));
  }
}

/** Point quadtree used for both viewport culling and logarithmic hover lookup. */
export class PointQuadtree {
  private readonly root: QuadNode;

  constructor(bounds: SpatialBounds) {
    const pad = 1;
    this.root = new QuadNode({
      x: bounds.x - pad,
      y: bounds.y - pad,
      width: Math.max(2, bounds.width + pad * 2),
      height: Math.max(2, bounds.height + pad * 2),
    }, 0);
  }

  insert(point: IndexedPoint): void {
    this.root.insert(point);
  }

  query(range: SpatialBounds): IndexedPoint[] {
    const result: IndexedPoint[] = [];
    this.root.query(range, result);
    return result;
  }

  nearest(x: number, y: number, radius: number): IndexedPoint | null {
    const candidates = this.query({ x: x - radius, y: y - radius, width: radius * 2, height: radius * 2 });
    let nearest: IndexedPoint | null = null;
    let bestDistance = radius * radius;
    for (const candidate of candidates) {
      const distance = (candidate.x - x) ** 2 + (candidate.y - y) ** 2;
      if (distance <= bestDistance) {
        nearest = candidate;
        bestDistance = distance;
      }
    }
    return nearest;
  }
}
